import { useState } from "react";
import { Navigate } from "react-router-dom";
import { ForgotDialog } from "@/components/ForgotDialog";
import { Boxes, QrCode, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LanguageSelect } from "@/components/LanguageSelect";
import { Logo } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useErrorToast } from "@/lib/hooks";
import { useAuth } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

const HERO = "https://images.unsplash.com/photo-1767602201214-4517f78136f2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const BrandPanel = () => {
  const { t } = useI18n();
  const feats = [[Boxes, "auth.feature1"], [QrCode, "auth.feature2"], [Sparkles, "auth.feature3"]];
  return (
    <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-slate-900 p-12 text-white">
      <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-900/30" />
      <div className="relative"><Logo light /></div>
      <div className="relative space-y-8 fade-up">
        <div className="font-mono text-xs text-blue-200 flex flex-wrap items-center gap-1.5">
          {["Maison", "Garage", "Étagère 1", "Carton A"].map((s, i, a) => (
            <span key={s} className="flex items-center gap-1.5">
              <span className={i === a.length - 1 ? "rounded bg-blue-600 px-2 py-1 text-white" : "rounded border border-white/20 px-2 py-1"}>{s}</span>
              {i < a.length - 1 && <span className="text-white/40">→</span>}
            </span>
          ))}
        </div>
        <h1 className="text-5xl xl:text-6xl font-extrabold leading-[1.05] max-w-lg">{t("app.tagline")}</h1>
        <p className="text-slate-300 max-w-md">{t("app.taglineSub")}</p>
        <ul className="space-y-3">
          {feats.map(([Icon, k]) => <li key={k} className="flex items-center gap-3 text-sm"><Icon className="w-4 h-4 text-amber-400" />{t(k)}</li>)}
        </ul>
      </div>
      <p className="relative text-xs text-slate-400 flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" />{t("auth.trial")}</p>
    </div>
  );
};

export default function AuthPage() {
  const { t, lang } = useI18n();
  const { me, signIn } = useAuth();
  const showErr = useErrorToast();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  if (me) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = mode === "login" ? { email: form.email, password: form.password } : { ...form, language: lang };
      const { data } = await api.post(`/auth/${mode}`, body);
      await signIn(data);
    } catch (err) { showErr(err); } finally { setBusy(false); }
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <BrandPanel />
      <div className="flex flex-col grid-paper">
        <div className="flex items-center justify-between p-5 lg:justify-end">
          <span className="lg:hidden"><Logo /></span>
          <LanguageSelect className="w-40" />
        </div>
        <div className="flex flex-1 items-center justify-center px-5 pb-12">
          <div className="w-full max-w-sm space-y-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm fade-up">
            <h2 className="lg:hidden text-3xl font-extrabold">{t("app.tagline")}</h2>
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="grid grid-cols-2 w-full h-11">
                <TabsTrigger value="login" data-testid="auth-tab-login">{t("auth.login")}</TabsTrigger>
                <TabsTrigger value="register" data-testid="auth-tab-register">{t("auth.register")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div className="space-y-1.5"><Label>{t("auth.name")}</Label>
                  <Input data-testid="auth-name-input" className="h-12" value={form.name} onChange={set("name")} required /></div>
              )}
              <div className="space-y-1.5"><Label>{t("auth.email")}</Label>
                <Input data-testid="auth-email-input" className="h-12" type="email" value={form.email} onChange={set("email")} required autoComplete="email" /></div>
              <div className="space-y-1.5"><Label>{t("auth.password")}</Label>
                <Input data-testid="auth-password-input" className="h-12" type="password" minLength={6} value={form.password} onChange={set("password")} required /></div>
              <Button data-testid="auth-submit-button" type="submit" disabled={busy} className="w-full h-12 text-base font-semibold">
                {mode === "login" ? t("auth.login") : t("auth.register")}
              </Button>
            </form>
            {mode === "login" && <ForgotDialog />}
            <div className="flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />{t("auth.or")}<span className="h-px flex-1 bg-slate-200" /></div>
            <Button data-testid="auth-google-button" variant="outline" onClick={google} className="w-full h-12 gap-2">
              <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
              {t("auth.google")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
